import { useContext, useMemo } from 'react';
import AnimateHeight from '@/common/AnimateHeight';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import FacetDataContext, {
  useFacetData
} from '~/context/common/FacetDataContext';
import type {
  AppliedFilters,
  CommonModelFacet,
  MakesFacet,
  MakeType,
  ModelsKey,
  TrimFacet
} from '~/contracts/common';
import Checkbox from './Checkbox';
import FilterDrawer from './FilterDrawer';

type SelectedModel = {
  make: string;
  model: string;
  modelsKey: ModelsKey;
  trims?: Array<TrimFacet>;
};

function useAppliedMakes(appliedFilters: AppliedFilters): MakeType[] {
  return useMemo(
    () =>
      (appliedFilters.makes ?? []).sort((a, b) => a.name.localeCompare(b.name)),
    [appliedFilters.makes]
  );
}

function useFacetDataForSelectedModels(
  appliedMakes: Array<MakeType>,
  makes: MakesFacet = {}
): SelectedModel[] {
  return useMemo(
    () =>
      appliedMakes.flatMap(appliedMake => {
        if (!(appliedMake.name in makes)) return [];

        const make = makes[appliedMake.name];
        const modelsKey: ModelsKey = appliedMake.models
          ? 'models'
          : 'parentModels';
        const appliedModels = appliedMake[modelsKey] ?? [];
        const modelsToCheck: CommonModelFacet[] = appliedMake.models
          ? make.models ?? []
          : make.parentModels ?? [];

        return appliedModels
          ? appliedModels
              .flatMap(appliedModel => {
                const model = modelsToCheck.find(
                  m => m.key === appliedModel.name
                );

                return model
                  ? {
                      make: make.key,
                      model: model.key,
                      modelsKey,
                      trims: model.trims
                    }
                  : [];
              })
              .sort((a, b) => a.model.localeCompare(b.model))
          : [];
      }),
    [appliedMakes, makes]
  );
}

function getIsSelected(
  makes: MakeType[],
  makeName: string,
  modelsKey: ModelsKey,
  modelName: string,
  trim: string
): boolean {
  const make = makes.find(m => m.name === makeName);
  const model = make?.[modelsKey]?.find(m => m.name === modelName);

  return model?.trims?.includes(trim) ?? false;
}

export default function Trim() {
  const { appliedFilters, updateMakes } = useContext(AppliedFiltersContext);
  const { showInventoryCounts } = useContext(FacetDataContext);
  const facetData = useFacetData();

  const appliedMakes = useAppliedMakes(appliedFilters);
  const selectedModels = useFacetDataForSelectedModels(
    appliedMakes,
    facetData.makes
  );

  const includeMakeInDisplay = appliedMakes.length > 1;
  const show = selectedModels.length > 0;

  return (
    <AnimateHeight show={show}>
      <FilterDrawer
        testId="facet-trims"
        facetContent={appliedMakes}
        facetName="Trim"
      >
        <div className="grid">
          {selectedModels.map(({ make, model, modelsKey, trims }) => (
            <div
              key={`${model}-trims`}
              className="grid gap-xl border-b border-solid border-border-subtle py-2xl first:pt-0 last:border-0 last:pb-0"
            >
              <h4 className="text-text-strong t-label-sm">
                {includeMakeInDisplay ? make : ''} {model}
              </h4>
              <div className="grid gap-xl">
                {trims?.map(({ count, key: trim }) => (
                  <Checkbox
                    checked={getIsSelected(
                      appliedMakes,
                      make,
                      modelsKey,
                      model,
                      trim
                    )}
                    facet="trims"
                    filter={trim}
                    id={`trim-${model}-${trim}`}
                    key={trim}
                    onChange={() =>
                      updateMakes({
                        modelsKey,
                        name: trim,
                        parents: [make, model]
                      })
                    }
                    label={
                      showInventoryCounts
                        ? `${trim} (${count.toLocaleString()})`
                        : trim
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </FilterDrawer>
    </AnimateHeight>
  );
}
